import React from 'react';
import { Table } from 'antd';
import { Eye, Pencil, TriangleAlert, Calendar, Flag } from 'lucide-react';
import dayjs from 'dayjs';
import { SectionCard, StatusTag, IconAction, IconActions, EmptyState } from '../../common';
import { isExpired, recordStatus } from '../../../utils/recordStatus';
import { getTotalInvestment } from '../../../utils/tradeInvestment';
import { formatCurrency } from '../../../utils/format';

/** Date cell; highlighted when `danger`. */
const DateCell = ({ value, danger = false }) => (
    <span className={`inline-flex items-center gap-1.5 tabular-nums ${danger ? 'text-danger-fg font-medium' : 'text-surface-600'}`}>
        <Calendar className={`text-[13px] ${danger ? '' : 'text-surface-400'}`} />
        {value ? dayjs(value).format('DD/MM/YYYY') : 'N/A'}
    </span>
);

const CampaignsTable = ({ rows, loading, navigateCampaign, onEdit }) => {
    const columns = [
        {
            title: 'Campaña',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex flex-col items-start gap-1 py-0.5">
                    <button
                        type="button"
                        onClick={() => navigateCampaign(record._id)}
                        className="p-0 bg-transparent border-0 cursor-pointer text-left text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors">
                        {text}
                    </button>
                    {isExpired(record.expirationDate) && (
                        <StatusTag tone="warning" icon={<TriangleAlert />}>
                            Expirado
                        </StatusTag>
                    )}
                </div>
            ),
            sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
        },
        {
            title: 'Tipo',
            dataIndex: 'campaignType',
            key: 'campaignType',
            render: (text) => <StatusTag tone={text === 'pop_blando' ? 'info' : 'violet'}>{text === 'pop_blando' ? 'POP Blando' : 'POP Duro'}</StatusTag>,
            filters: [
                { text: 'POP Blando', value: 'pop_blando' },
                { text: 'POP Duro', value: 'pop_duro' }
            ],
            onFilter: (value, record) => record.campaignType === value
        },
        {
            title: 'Inversión',
            key: 'investment',
            align: 'right',
            render: (_, record) => <span className="tabular-nums font-semibold text-surface-900">{formatCurrency(getTotalInvestment(record))}</span>,
            sorter: (a, b) => getTotalInvestment(a) - getTotalInvestment(b)
        },
        {
            title: 'Inicia',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (text) => <DateCell value={text} />,
            sorter: (a, b) => new Date(a.startDate) - new Date(b.startDate)
        },
        {
            title: 'Vence',
            dataIndex: 'expirationDate',
            key: 'expirationDate',
            render: (text) => <DateCell value={text} danger={isExpired(text)} />,
            sorter: (a, b) => new Date(a.expirationDate) - new Date(b.expirationDate)
        },
        {
            title: 'Estado',
            key: 'status',
            render: (_, record) => {
                const status = recordStatus(record);
                return (
                    <StatusTag tone={status.tone} dot>
                        {status.label}
                    </StatusTag>
                );
            },
            filters: [
                { text: 'Activo', value: 'active' },
                { text: 'Expirado', value: 'expired' },
                { text: 'Inactivo', value: 'inactive' }
            ],
            onFilter: (value, record) => {
                if (value === 'expired') return isExpired(record.expirationDate);
                if (value === 'active') return record.active && !isExpired(record.expirationDate);
                if (value === 'inactive') return !record.active;
                return true;
            }
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 100,
            render: (_, record) => (
                <IconActions>
                    <IconAction icon={<Eye />} label="Ver" onClick={() => navigateCampaign(record._id)} />
                    <IconAction icon={<Pencil />} label="Editar" onClick={() => onEdit(record)} />
                </IconActions>
            )
        }
    ];

    return (
        <SectionCard title="Campañas" icon={<Flag />} flush>
            <Table
                columns={columns}
                dataSource={rows}
                loading={loading}
                rowKey="_id"
                size="middle"
                scroll={{ x: 'max-content' }}
                locale={{
                    emptyText: <EmptyState icon={<Flag />} title="No hay campañas" description="Ajusta la búsqueda o crea una nueva campaña." />
                }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} campañas`
                }}
            />
        </SectionCard>
    );
};

export default CampaignsTable;
